import { useCallback, useEffect, useMemo, useState } from "react"

import { Col, Flex, Row, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import Filters from "../../../Components/Filters"
import TableComponent from "../../../Components/Table"
import { skeletonColumns, skeletonRows } from "../../../Components/Table/skeleton"
import { RoutesPaths } from "../../../constants/user-routes"
import Icon from "../../../Icon"
import { toApiOrdering } from "../../ReceiptPage/api/params"
import { getDeflectLookupDescriptors } from "../api/services"
import { activityColumns, RESULT_OPTIONS } from "../components/activityColumns"
import DeflectFilters from "../components/DeflectFilters"
import { useDeflectFilters } from "../hooks/useDeflectFilters"
import { useDeflectScope } from "../hooks/useDeflectScope"
import { usePageOf } from "../hooks/usePageOf"
import { DEFLECT_SECTION } from "../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../redux/deflectAnalytics.slice"
import { CHANNEL_COLORS, channelLabel, PROGRAM_OPTIONS } from "../utils/vocabulary"

import "../../DashboardPage/styles.less"
import tables from "../tables.module.less"

const { Title } = Typography

const PAGE_SIZE = 50
const DEFAULT_ORDERING = "-date"

const ALL_FILTERS = { channel: "all", program: "all", result: "all", descriptor: "all" }

const picked = (value) => (Array.isArray(value) && value.length ? value : null)

const LookupActivityPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { getBase } = useDeflectScope(RoutesPaths.deflect.analytics_activity)
  const activity = useSelector(selectDeflectSection(DEFLECT_SECTION.LOOKUP_ACTIVITY))
  const fetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.LOOKUP_ACTIVITY))
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.LOOKUP_ACTIVITY))
  const isMobile = useSelector((s) => s.nav.isMobile)
  const businessAccountId = useSelector((s) => s.auth.userInfo?.current_business_account_id)

  const {
    filters: { program: scopePrograms }
  } = useDeflectFilters(RoutesPaths.deflect.analytics_activity)

  // A flow node opens on the Analytics page's programs unless it names a program itself.
  const [filters, setFilters] = useState(() => ({
    ...ALL_FILTERS,
    ...(scopePrograms?.length ? { program: scopePrograms } : {}),
    ...state?.drill
  }))
  const [ordering, setOrdering] = useState(DEFAULT_ORDERING)
  const [descriptors, setDescriptors] = useState([])

  useEffect(() => {
    getDeflectLookupDescriptors().then((response) => {
      if (response.ok) setDescriptors(response.data?.descriptors ?? [])
    })
  }, [businessAccountId])

  const filterOptions = useMemo(
    () => ({
      channel: Object.keys(CHANNEL_COLORS).map((id) => ({ id, name: t(channelLabel(id)) })),
      program: PROGRAM_OPTIONS.map(({ id, name }) => ({ id, name: t(name) })),
      result: RESULT_OPTIONS.map(({ id, name }) => ({ id, name: t(name) })),
      descriptor: descriptors.map((descriptor) => ({ id: descriptor, name: descriptor }))
    }),
    [descriptors, t]
  )

  const query = useMemo(() => {
    const programs = picked(filters.program)

    return {
      ordering: toApiOrdering(ordering),
      ...(picked(filters.channel) ? { channel: filters.channel.join(",") } : {}),
      program: programs ? programs.join(",") : "all",
      ...(picked(filters.result) ? { outcome: filters.result.join(",") } : {}),
      descriptor: picked(filters.descriptor) ?? []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, ordering, getBase])
  const [page, setPage] = usePageOf(query)

  useEffect(() => {
    getBase(DEFLECT_SECTION.LOOKUP_ACTIVITY, { ...query, page, page_size: PAGE_SIZE })
  }, [getBase, query, page])

  const handleChangeFilter = useCallback((patch, type) => {
    if (type !== "changeVisible") setFilters((current) => ({ ...current, ...patch }))
  }, [])

  const handleTableChange = (pagination, _filters, sorter) => {
    const { field, order } = (Array.isArray(sorter) ? sorter[0] : sorter) || {}
    setOrdering(order ? `${order === "descend" ? "-" : ""}${field}` : DEFAULT_ORDERING)
    setPage(pagination.current)
  }

  const rows = activity?.results ?? []
  const columns = useMemo(() => activityColumns({ t }), [t])
  const loadingColumns = useMemo(() => skeletonColumns(columns), [columns])
  const isDefault = Object.keys(ALL_FILTERS).every((key) => !picked(filters[key]))

  useEffect(() => {
    document.title = "Lookup activity - Chargeback"
    document.getElementById("global-wrap")?.scrollTo(0, 0)
  }, [])

  return (
    <Row gutter={[0, isMobile ? 12 : 32]} className="dashboard-page">
      <Col span={24}>
        <ButtonComponent
          type="text"
          onClick={() => navigate(RoutesPaths.deflect.analytics)}
          icon={<Icon type="chevron_left_small" />}
          style={{ marginLeft: -12 }}
        >
          {t("Back to Analytics")}
        </ButtonComponent>
      </Col>

      <Col span={24}>
        <Title level={4} style={{ marginBottom: 0 }}>
          {t("Lookup activity")}
        </Title>
      </Col>

      <Col span={24}>
        <Flex
          gap={16}
          justify="space-between"
          align={isMobile ? "stretch" : "center"}
          style={{ flexDirection: isMobile && "column" }}
        >
          <Filters
            filtersType="deflect_activity"
            visibleFilters={{ channel: true, program: true, result: true, descriptor: true }}
            filterOptions={filterOptions}
            allFilters={[
              { field_id: "channel", label: "Channel" },
              { field_id: "program", label: "Program" },
              { field_id: "result", label: "Result" },
              { field_id: "descriptor", label: "Descriptor" }
            ]}
            filters={filters}
            onChangeFilter={handleChangeFilter}
            withoutAdd
            showClearBtn={!isDefault}
            onClearFilters={() => setFilters(ALL_FILTERS)}
            isMobile={isMobile}
          />
          <DeflectFilters
            page={RoutesPaths.deflect.analytics_activity}
            withCompare={false}
            withProgram={false}
          />
        </Flex>
      </Col>

      <Col span={24}>
        <TableComponent
          className={tables.olympics}
          dataSource={firstLoad ? skeletonRows(PAGE_SIZE) : rows}
          columns={firstLoad ? loadingColumns : columns}
          loading={fetching && !firstLoad}
          withOutPagination={firstLoad}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: activity?.count ?? 0,
            showSizeChanger: false
          }}
          onChange={handleTableChange}
          rowKey={firstLoad ? "key" : "id"}
          showHeader={firstLoad || !!rows.length}
          locale={{
            emptyText: isDefault ? (
              <EmptyComponent type="no_data_available" style={{ minHeight: 450 }} />
            ) : (
              <EmptyComponent type="search" style={{ minHeight: 450 }} />
            )
          }}
        />
      </Col>
    </Row>
  )
}

export default LookupActivityPage
